import { ICourse } from '@/types/courses';
import Text from '@ui/text/text/text';
import { useCallback, useEffect, useRef, useState } from 'react';
import NumericStepper from '@ui/numeric-stepper/numeric-stepper';
import { numClamp } from '@/utils/num-clamp';
import Container from '@ui/container/container';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import CoursesCategoriesSelect from '../controls/courses-categories-select';
import { defaultCategories } from '../utils/course-requirement-defaults';
import EditStateTextAreaWrapper from '@ui/edit-state-wrapper/edit-state-text-area-wrapper';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Popover from '@ui/popover/popover';
import CourseDeleteModal from '../modals/course-delete-modal';
import CourseBadge from '../icons/course-badge';

type CourseDetailProps = {
  handleSave: (name?: string, description?: string, categories?: string[], credits?: number) => void;
  course: ICourse | null;
  onDelete: () => void;
  working: boolean;
};

function CourseDetail({ handleSave, course, onDelete, working }: CourseDetailProps) {
  const [courseDescription, setCourseDescription] = useState(course?.description);
  const [newCourseName, setNewCourseName] = useState(course?.name.base || '');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const descriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toggleDeleteModal = () => setDeleteModalOpen((prevState) => !prevState);

  useEffect(() => {
    setCourseDescription(course?.description);
  }, [course?.description]);

  useEffect(() => {
    setNewCourseName(course?.name.base || '');
  }, [course?.name.base]);

  const handleAddCategories = (selectedCategories: TReactSelectOption[]) => {
    const updatedCategories = selectedCategories.map((c) => c.value.toString());
    handleSave('', '', updatedCategories);
  };

  const debouncedSaveDescription = useCallback(
    (value: string) => {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }

      descriptionTimeoutRef.current = setTimeout(() => {
        handleSave('', value);
      }, 500);
    },
    [handleSave]
  );

  useEffect(() => {
    return () => {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
    };
  }, []);

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setCourseDescription(value);
      debouncedSaveDescription(value);
    },
    [debouncedSaveDescription]
  );

  const handleDescriptionBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
      handleSave('', e.target.value);
    },
    [handleSave]
  );

  return (
    <>
      <Container className="flex ai-center">
        <Text className="H5" color="var(--gray-100)">
          Course details
        </Text>
      </Container>
      <Container className="radius-12 padding-20 flex-column gap-10" background="var(--gray-900)" minHeight="166px">
        <Container className="flex ai-center jc-center header gap-10">
          <div className="position-relative min-width-40">
            <CourseBadge height={40} width={40} startColor="#5AE4FF" endColor="#A7FF7B" />
            <Icon
              className="position-absolute top-13 left-13"
              name={ICONS.ACADEMIC_CAP}
              size={16}
              color="var(--gray-1200)"
            />
          </div>
          <InputWithOverlay
            wrapperClass="flex-1"
            border="solid 1px transparent"
            hoverBackground="var(--gray-900)"
            hoverBorder="solid 1px var(--gray-300)"
            inputProps={{
              value: newCourseName || '',
              placeholder: 'Give your Course a name',
              className: 'radius-4 H2 padding-0',
              onChange: (e) => setNewCourseName(e.target.value),
              onBlur: (e) => handleSave(e.target.value),
            }}
          />
          <Popover
            icon={ICONS.THREE_DOTS_VERTICAL}
            buttonHoverColor="var(--gray-700)"
            width="fit-content"
            popoverWidth="fit-content"
            forcePosition="bottom-left"
            contentClassName="margin-t-10"
          >
            <Button
              data-testid="course-edit-delete-btn"
              onClick={toggleDeleteModal}
              backgroundColor="var(--gray-800)"
              hoverBackgroundColor="var(--gray-700)"
            >
              <Text className="L1" color="var(--semantic-red-500)" whiteSpace="nowrap">
                Delete Course
              </Text>
            </Button>
          </Popover>
        </Container>
        <Container className="flex jc-space-between">
          <Container className="min-width-540 flex-column gap-10 flex-1" style={{ maxWidth: '600px' }}>
            <Text className="L3" color="var(--gray-200)">
              ABOUT THIS COURSE
            </Text>
            <EditStateTextAreaWrapper
              maxRowsFocused={3}
              maxRowsBlurred={2}
              textAreaProps={{
                value: courseDescription || '',
                onChange: handleDescriptionChange,
                onBlur: handleDescriptionBlur,
                placeholder: 'Add a description to give your audience more context.',
              }}
            />
          </Container>
          <Container className="flex-column gap-10">
            <Text className="L3" color="var(--gray-200)">
              CREDITS
            </Text>
            <NumericStepper
              initialValue={course?.credits}
              minValue={0}
              maxValue={10000}
              step={1}
              onChange={(e) => {
                const credits = numClamp(e, 0, 10000);
                handleSave('', '', [], credits);
              }}
            />
          </Container>
          <Container className="flex-column gap-10">
            <Text className="L2" color="var(--gray-200)">
              CATEGORIES
            </Text>
            <Container width="164px">
              <CoursesCategoriesSelect
                defaultCategories={defaultCategories(course)}
                onMenuClose={handleAddCategories}
                viewableItems={1}
                controlWidth={'164px'}
              />
            </Container>
          </Container>
        </Container>
        <CourseDeleteModal
          showDeleteModal={deleteModalOpen}
          setShowDeleteModal={toggleDeleteModal}
          courseName={course?.name.base || ''}
          onDelete={onDelete}
          working={working}
        />
      </Container>
    </>
  );
}

export default CourseDetail;
